import { ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Input,
  Modal,
  message,
  Segmented,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Empty from '@/components/QuietEmpty';
import { formatCrmDate } from '@/modules/zy-crm/utils/display';
import { queryContacts } from '@/services/bff/contact.api';
import { queryCrmResource } from '@/services/bff/crm.api';
import { queryCustomers } from '@/services/bff/customer.api';
import { queryLeads } from '@/services/bff/lead.api';
import type { CalendarBizType } from '@/services/bff/types/calendar';
import { getCurrentMemberUuid } from '@/utils/currentMember';

export type BusinessRelationValueItem = {
  bizType: CalendarBizType;
  bizUuid: string;
};

type BusinessRelationSelectorModalProps = {
  open: boolean;
  value?: BusinessRelationValueItem[];
  defaultBizType?: CalendarBizType;
  allowedBizTypes?: CalendarBizType[];
  title?: string;
  onCancel: () => void;
  onOk: (items: BusinessRelationValueItem[]) => void;
};

type ViewMode = 'all' | 'mine';

type BusinessQueryState = {
  page: number;
  pageSize: number;
  keyword: string;
  viewMode: ViewMode;
};

type BusinessRow = Record<string, unknown> & {
  uuid: string;
};

type BusinessTypeConfig = {
  bizType: CalendarBizType;
  label: string;
  buildColumns: () => ColumnsType<BusinessRow>;
  queryPage: (query: BusinessQueryState) => Promise<{
    list: BusinessRow[];
    total: number;
  }>;
};

const DEFAULT_PAGE_SIZE = 10;

export const BUSINESS_RELATION_BIZ_TYPES: CalendarBizType[] = [
  'lead',
  'customer',
  'contact',
  'opportunity',
  'quote',
  'contract',
  'receipt',
];

export const BUSINESS_RELATION_TYPE_LABELS: Record<CalendarBizType, string> = {
  lead: '线索',
  customer: '客户',
  contact: '联系人',
  opportunity: '商机',
  quote: '报价单',
  contract: '合同',
  receipt: '回款',
};

const createDefaultQueryState = (): BusinessQueryState => ({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  keyword: '',
  viewMode: 'all',
});

const buildInitialQueryStateMap = (): Record<
  CalendarBizType,
  BusinessQueryState
> => ({
  lead: createDefaultQueryState(),
  customer: createDefaultQueryState(),
  contact: createDefaultQueryState(),
  opportunity: createDefaultQueryState(),
  quote: createDefaultQueryState(),
  contract: createDefaultQueryState(),
  receipt: createDefaultQueryState(),
});

const normalizeText = (value: unknown) => String(value || '').trim();

const normalizeMoney = (value: unknown) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return '-';
  }
  return amount.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const buildOwnerFilter = (query: BusinessQueryState) => {
  if (query.viewMode !== 'mine') {
    return {};
  }
  const ownerUserUuid = getCurrentMemberUuid();
  return ownerUserUuid ? { ownerUserUuid } : {};
};

const buildBaseParams = (query: BusinessQueryState) => ({
  page: query.page,
  pageSize: query.pageSize,
  ...(query.keyword ? { keyword: query.keyword } : {}),
  ...buildOwnerFilter(query),
});

const BIZ_TYPE_CONFIGS: Record<CalendarBizType, BusinessTypeConfig> = {
  lead: {
    bizType: 'lead',
    label: BUSINESS_RELATION_TYPE_LABELS.lead,
    buildColumns: () => [
      { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
      { title: '手机', dataIndex: 'mobile', key: 'mobile', width: 140 },
      {
        title: '负责人',
        key: 'ownerUserName',
        width: 140,
        render: (_, record) =>
          normalizeText(record.ownerUserName) ||
          normalizeText(record.ownerName) ||
          normalizeText(record.ownerMemberName) ||
          '-',
      },
      {
        title: '下次跟进时间',
        dataIndex: 'contactNextTime',
        key: 'contactNextTime',
        width: 180,
        render: (value) => formatCrmDate(value),
      },
    ],
    queryPage: async (query) => {
      const page = await queryLeads(buildBaseParams(query));
      return {
        list: (page.list || []).map((item) => item as unknown as BusinessRow),
        total: page.total || 0,
      };
    },
  },
  customer: {
    bizType: 'customer',
    label: BUSINESS_RELATION_TYPE_LABELS.customer,
    buildColumns: () => [
      { title: '客户名称', dataIndex: 'name', key: 'name', width: 180 },
      { title: '电话', dataIndex: 'telephone', key: 'telephone', width: 140 },
      {
        title: '负责人',
        key: 'ownerUserName',
        width: 140,
        render: (_, record) =>
          normalizeText(record.ownerUserName) ||
          normalizeText(record.ownerName) ||
          normalizeText(record.ownerMemberName) ||
          '-',
      },
      {
        title: '下次跟进时间',
        dataIndex: 'contactNextTime',
        key: 'contactNextTime',
        width: 180,
        render: (value) => formatCrmDate(value),
      },
    ],
    queryPage: async (query) => {
      const page = await queryCustomers(buildBaseParams(query));
      return {
        list: (page.list || []).map((item) => item as unknown as BusinessRow),
        total: page.total || 0,
      };
    },
  },
  contact: {
    bizType: 'contact',
    label: BUSINESS_RELATION_TYPE_LABELS.contact,
    buildColumns: () => [
      { title: '联系人姓名', dataIndex: 'name', key: 'name', width: 160 },
      { title: '手机', dataIndex: 'mobile', key: 'mobile', width: 140 },
      {
        title: '所属客户',
        key: 'customerName',
        width: 180,
        render: (_, record) =>
          normalizeText(record.customerName) ||
          normalizeText(record.customer) ||
          '-',
      },
      {
        title: '负责人',
        key: 'ownerUserName',
        width: 140,
        render: (_, record) =>
          normalizeText(record.ownerUserName) ||
          normalizeText(record.ownerName) ||
          normalizeText(record.ownerMemberName) ||
          '-',
      },
    ],
    queryPage: async (query) => {
      const page = await queryContacts(buildBaseParams(query));
      return {
        list: (page.list || []).map((item) => item as unknown as BusinessRow),
        total: page.total || 0,
      };
    },
  },
  opportunity: {
    bizType: 'opportunity',
    label: BUSINESS_RELATION_TYPE_LABELS.opportunity,
    buildColumns: () => [
      { title: '商机名称', dataIndex: 'name', key: 'name', width: 180 },
      {
        title: '客户',
        key: 'customerName',
        width: 180,
        render: (_, record) =>
          normalizeText(record.customerName) ||
          normalizeText(record.customer) ||
          '-',
      },
      {
        title: '负责人',
        key: 'ownerUserName',
        width: 140,
        render: (_, record) =>
          normalizeText(record.ownerUserName) ||
          normalizeText(record.ownerName) ||
          normalizeText(record.ownerMemberName) ||
          '-',
      },
      {
        title: '预计成交时间',
        dataIndex: 'expectedDealDate',
        key: 'expectedDealDate',
        width: 180,
        render: (value) => formatCrmDate(value, 'date'),
      },
    ],
    queryPage: async (query) => {
      const page = await queryCrmResource<BusinessRow>(
        'opportunities',
        buildBaseParams(query),
      );
      return {
        list: page.list || [],
        total: page.total || 0,
      };
    },
  },
  quote: {
    bizType: 'quote',
    label: BUSINESS_RELATION_TYPE_LABELS.quote,
    buildColumns: () => [
      { title: '报价单名称', dataIndex: 'name', key: 'name', width: 180 },
      { title: '报价编号', dataIndex: 'code', key: 'code', width: 160 },
      {
        title: '客户',
        key: 'customerName',
        width: 180,
        render: (_, record) =>
          normalizeText(record.customerName) ||
          normalizeText(record.customer) ||
          '-',
      },
      {
        title: '报价日期',
        dataIndex: 'quoteDate',
        key: 'quoteDate',
        width: 160,
        render: (value) => formatCrmDate(value, 'date'),
      },
    ],
    queryPage: async (query) => {
      const page = await queryCrmResource<BusinessRow>(
        'quotes',
        buildBaseParams(query),
      );
      return {
        list: page.list || [],
        total: page.total || 0,
      };
    },
  },
  contract: {
    bizType: 'contract',
    label: BUSINESS_RELATION_TYPE_LABELS.contract,
    buildColumns: () => [
      { title: '合同名称', dataIndex: 'name', key: 'name', width: 180 },
      { title: '合同编号', dataIndex: 'code', key: 'code', width: 160 },
      {
        title: '客户',
        key: 'customerName',
        width: 180,
        render: (_, record) =>
          normalizeText(record.customerName) ||
          normalizeText(record.customer) ||
          '-',
      },
      {
        title: '签约日期',
        dataIndex: 'orderDate',
        key: 'orderDate',
        width: 160,
        render: (value) => formatCrmDate(value, 'date'),
      },
    ],
    queryPage: async (query) => {
      const page = await queryCrmResource<BusinessRow>(
        'contracts',
        buildBaseParams(query),
      );
      return {
        list: page.list || [],
        total: page.total || 0,
      };
    },
  },
  receipt: {
    bizType: 'receipt',
    label: BUSINESS_RELATION_TYPE_LABELS.receipt,
    buildColumns: () => [
      { title: '回款编号', dataIndex: 'code', key: 'code', width: 160 },
      {
        title: '客户',
        key: 'customerName',
        width: 180,
        render: (_, record) =>
          normalizeText(record.customerName) ||
          normalizeText(record.customer) ||
          '-',
      },
      {
        title: '合同',
        key: 'contractName',
        width: 180,
        render: (_, record) =>
          normalizeText(record.contractName) ||
          normalizeText(record.contract) ||
          '-',
      },
      {
        title: '回款日期',
        dataIndex: 'receiptDate',
        key: 'receiptDate',
        width: 160,
        render: (value) => formatCrmDate(value, 'date'),
      },
      {
        title: '回款金额',
        key: 'amount',
        width: 160,
        render: (_, record) =>
          normalizeMoney(
            record.amount ?? record.receiptAmount ?? record.totalPrice,
          ),
      },
    ],
    queryPage: async (query) => {
      const page = await queryCrmResource<BusinessRow>(
        'receipts',
        buildBaseParams(query),
      );
      return {
        list: page.list || [],
        total: page.total || 0,
      };
    },
  },
};

const buildSelectedState = (
  items?: BusinessRelationValueItem[],
  bizTypes: CalendarBizType[] = BUSINESS_RELATION_BIZ_TYPES,
): Record<CalendarBizType, string[]> => {
  const nextState: Record<CalendarBizType, string[]> = {
    lead: [],
    customer: [],
    contact: [],
    opportunity: [],
    quote: [],
    contract: [],
    receipt: [],
  };

  for (const item of items || []) {
    const bizType = item?.bizType;
    const bizUuid = normalizeText(item?.bizUuid);
    if (!bizType || !bizUuid || !bizTypes.includes(bizType)) {
      continue;
    }
    if (!nextState[bizType].includes(bizUuid)) {
      nextState[bizType].push(bizUuid);
    }
  }

  return nextState;
};

const toOutputItems = (
  selectedState: Record<CalendarBizType, string[]>,
  bizTypes: CalendarBizType[] = BUSINESS_RELATION_BIZ_TYPES,
): BusinessRelationValueItem[] =>
  bizTypes.flatMap((bizType) =>
    (selectedState[bizType] || []).map((bizUuid) => ({
      bizType,
      bizUuid,
    })),
  );

export function BusinessRelationSelectorModal({
  open,
  value,
  defaultBizType = 'lead',
  allowedBizTypes,
  title = '选择关联数据',
  onCancel,
  onOk,
}: BusinessRelationSelectorModalProps) {
  const visibleBizTypes = useMemo(() => {
    const requested = new Set(allowedBizTypes || BUSINESS_RELATION_BIZ_TYPES);
    const normalized = BUSINESS_RELATION_BIZ_TYPES.filter((bizType) =>
      requested.has(bizType),
    );
    return normalized.length ? normalized : BUSINESS_RELATION_BIZ_TYPES;
  }, [allowedBizTypes]);
  const [activeBizType, setActiveBizType] = useState<CalendarBizType>(() =>
    visibleBizTypes.includes(defaultBizType)
      ? defaultBizType
      : visibleBizTypes[0],
  );
  const [queryStateMap, setQueryStateMap] = useState<
    Record<CalendarBizType, BusinessQueryState>
  >(buildInitialQueryStateMap);
  const [selectedState, setSelectedState] = useState<
    Record<CalendarBizType, string[]>
  >(buildSelectedState(value));
  const [countMap, setCountMap] = useState<Record<CalendarBizType, number>>({
    lead: 0,
    customer: 0,
    contact: 0,
    opportunity: 0,
    quote: 0,
    contract: 0,
    receipt: 0,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [tableData, setTableData] = useState<BusinessRow[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const tableRequestIdRef = useRef(0);
  const countRequestIdRef = useRef(0);

  const activeQueryState = queryStateMap[activeBizType];
  const activeConfig = BIZ_TYPE_CONFIGS[activeBizType];

  const tableColumns = useMemo(
    () => activeConfig.buildColumns(),
    [activeConfig, activeBizType],
  );

  const selectedRowKeys = selectedState[activeBizType] || [];
  const selectedCount = useMemo(
    () => toOutputItems(selectedState, visibleBizTypes).length,
    [selectedState, visibleBizTypes],
  );

  const loadCounts = async (query: BusinessQueryState) => {
    const requestId = countRequestIdRef.current + 1;
    countRequestIdRef.current = requestId;
    setCountLoading(true);
    try {
      const nextCounts = await Promise.all(
        visibleBizTypes.map(async (bizType) => {
          const config = BIZ_TYPE_CONFIGS[bizType];
          const result = await config.queryPage({
            ...query,
            page: 1,
            pageSize: 1,
          });
          return [bizType, result.total] as const;
        }),
      );
      if (requestId !== countRequestIdRef.current) {
        return;
      }
      setCountMap(
        nextCounts.reduce(
          (acc, [bizType, total]) => ({ ...acc, [bizType]: total }),
          {
            lead: 0,
            customer: 0,
            contact: 0,
            opportunity: 0,
            quote: 0,
            contract: 0,
            receipt: 0,
          } as Record<CalendarBizType, number>,
        ),
      );
    } catch (error) {
      console.error(error);
      message.error('业务数量加载失败');
    } finally {
      if (requestId === countRequestIdRef.current) {
        setCountLoading(false);
      }
    }
  };

  const loadTable = async (
    bizType: CalendarBizType,
    query: BusinessQueryState,
  ) => {
    const requestId = tableRequestIdRef.current + 1;
    tableRequestIdRef.current = requestId;
    setTableLoading(true);
    try {
      const result = await BIZ_TYPE_CONFIGS[bizType].queryPage(query);
      if (requestId !== tableRequestIdRef.current) {
        return;
      }
      setTableData(result.list || []);
      setTableTotal(result.total || 0);
    } catch (error) {
      console.error(error);
      if (requestId === tableRequestIdRef.current) {
        setTableData([]);
        setTableTotal(0);
      }
      message.error('业务数据加载失败');
    } finally {
      if (requestId === tableRequestIdRef.current) {
        setTableLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const normalizedValue = buildSelectedState(value, visibleBizTypes);
    setSelectedState(normalizedValue);
    const firstSelectedBizType = visibleBizTypes.find(
      (bizType) => normalizedValue[bizType].length > 0,
    );
    const fallbackBizType = visibleBizTypes.includes(defaultBizType)
      ? defaultBizType
      : visibleBizTypes[0];
    setActiveBizType(firstSelectedBizType || fallbackBizType);
  }, [defaultBizType, open, value, visibleBizTypes]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSearchInput(activeQueryState.keyword);
  }, [activeBizType, activeQueryState.keyword, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    void loadTable(activeBizType, activeQueryState);
  }, [activeBizType, activeQueryState, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    void loadCounts(activeQueryState);
  }, [
    activeBizType,
    activeQueryState.keyword,
    activeQueryState.viewMode,
    open,
  ]);

  const updateActiveQueryState = (
    patch:
      | Partial<BusinessQueryState>
      | ((prev: BusinessQueryState) => BusinessQueryState),
  ) => {
    setQueryStateMap((prev) => {
      const current = prev[activeBizType];
      const nextState =
        typeof patch === 'function' ? patch(current) : { ...current, ...patch };
      return {
        ...prev,
        [activeBizType]: nextState,
      };
    });
  };

  const handleSearch = (keyword: string) => {
    updateActiveQueryState((prev) => ({
      ...prev,
      keyword: keyword.trim(),
      page: 1,
    }));
  };

  const handleViewModeChange = (viewMode: ViewMode) => {
    updateActiveQueryState((prev) => ({
      ...prev,
      viewMode,
      page: 1,
    }));
  };

  const handleRefresh = () => {
    void loadTable(activeBizType, activeQueryState);
    void loadCounts(activeQueryState);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    updateActiveQueryState((prev) => ({
      ...prev,
      page,
      pageSize,
    }));
  };

  const tablePagination: TablePaginationConfig = {
    current: activeQueryState.page,
    pageSize: activeQueryState.pageSize,
    total: tableTotal,
    showSizeChanger: true,
    showTotal: (total) => `共 ${total} 条`,
    onChange: handlePaginationChange,
    onShowSizeChange: handlePaginationChange,
  };

  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      open={open}
      title={title}
      width={1120}
      destroyOnHidden
      onCancel={onCancel}
      onOk={() => onOk(toOutputItems(selectedState, visibleBizTypes))}
    >
      <div
        style={{
          display: 'flex',
          gap: 16,
          minHeight: 520,
        }}
      >
        <div
          style={{
            width: 180,
            borderRight: '1px solid #f0f0f0',
            paddingRight: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <Typography.Text strong>业务类型</Typography.Text>
            {countLoading ? <Spin size="small" /> : null}
          </div>
          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            {visibleBizTypes.map((bizType) => {
              const active = bizType === activeBizType;
              return (
                <Button
                  key={bizType}
                  block
                  type={active ? 'primary' : 'default'}
                  onClick={() => setActiveBizType(bizType)}
                  style={{
                    textAlign: 'left',
                    height: 'auto',
                    padding: '10px 12px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <span>{BUSINESS_RELATION_TYPE_LABELS[bizType]}</span>
                    <span>{countMap[bizType] || 0}</span>
                  </div>
                </Button>
              );
            })}
          </Space>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Space
            size={12}
            style={{
              width: '100%',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
            wrap
          >
            <Input.Search
              allowClear
              placeholder="搜索业务数据"
              value={searchInput}
              onChange={(event) => {
                const nextValue = event.target.value;
                setSearchInput(nextValue);
                if (nextValue === '') {
                  handleSearch('');
                }
              }}
              onSearch={(value) => handleSearch(value)}
              style={{ width: 280 }}
            />
            <Space>
              <Segmented<ViewMode>
                value={activeQueryState.viewMode}
                options={[
                  { label: '全部', value: 'all' },
                  { label: '我负责的', value: 'mine' },
                ]}
                onChange={(value) => handleViewModeChange(value)}
              />
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
            </Space>
          </Space>
          <div style={{ marginBottom: 12 }}>
            <Typography.Text type="secondary">
              已选择 {selectedCount} 条
            </Typography.Text>
          </div>
          <Table<BusinessRow>
            rowKey="uuid"
            size="small"
            loading={tableLoading}
            dataSource={tableData}
            columns={tableColumns}
            locale={{ emptyText: <Empty description={false} /> }}
            pagination={tablePagination}
            scroll={{ x: 900 }}
            rowSelection={{
              selectedRowKeys,
              preserveSelectedRowKeys: true,
              onChange: (keys) => {
                setSelectedState((prev) => ({
                  ...prev,
                  [activeBizType]: keys.map((item) => String(item)),
                }));
              },
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
